import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { type Variants, motion } from "framer-motion";
import { BadgeAlert } from "lucide-react";
import { type ReactNode, useMemo } from "react";

import { useReduced } from "@/components/motion/use-reduced";
import { ExamStatsCarouselView } from "@/components/report-card/exam-stats-carousel";
import { ReportCardsList } from "@/components/student/report-cards-list";
import { StudentProfileModern } from "@/components/student/student-profile-modern";
import { StudentReportCardsGraph } from "@/components/student/student-rc-graph";
import { currentRole, currentStudentId, pb } from "@/lib/pb";
import { useDocumentTitle } from "@/lib/use-document-title";

/**
 * Ported from portal-ui/app/(pages)/students/[studentId]/page.tsx +
 * components/student/index.tsx + components/student/profile.tsx.
 *
 * Layout matches portal-ui:
 *   StudentProfileModern (avatar + stats + contact line)
 *   Separator
 *   ExamStatsCarouselView (subject stat per-row carousel) — DEFERRED
 *   ReportCardsList (paginated RCs)
 *   StudentReportCardsGraph (mark trend chart) — DEFERRED
 *
 * The two deferred surfaces (carousel + trend graph) are independently
 * heavy (~450 LOC combined) and port in follow-up commits. The route here
 * renders the profile + RC list so the page is structurally and
 * visually aligned with portal-ui's main spine.
 */
interface StudentRecord {
  id: string;
  name: string;
  gender?: string;
  caste?: string;
  dob?: string;
  doj?: string;
  login?: string;
  // Phase 4: denormalized from auth, read directly off the record.
  email?: string;
  username?: string;
  phone?: string;
  expand?: {
    batch?: { id: string; name: string; exam_year?: string };
  };
}

function studentQuery(studentId: string, authId: string | undefined) {
  return {
    queryKey: ["student", studentId, authId ?? "anon"] as const,
    queryFn: () =>
      pb.collection("student").getOne<StudentRecord>(studentId, {
        expand: "batch",
        fields:
          "id,name,gender,caste,dob,doj,login,email,username,phone,expand.batch.id,expand.batch.name,expand.batch.exam_year",
      }),
  };
}

function rcCountQuery(studentId: string, authId: string | undefined) {
  return {
    queryKey: ["student", studentId, "rc-count", authId ?? "anon"] as const,
    queryFn: () =>
      pb.collection("report_card_combined").getList(1, 1, {
        filter: `student_id = '${studentId}'`,
        sort: "-result_date",
        fields: "id",
      }),
  };
}

function bestRankQuery(studentId: string, authId: string | undefined) {
  return {
    queryKey: ["student", studentId, "best-rank", authId ?? "anon"] as const,
    queryFn: () =>
      pb.collection("report_card_combined").getList<{ rank: number }>(1, 1, {
        filter: `student_id = '${studentId}' && rank > 0`,
        sort: "rank",
        skipTotal: true,
        fields: "rank",
      }),
  };
}

interface MarkStudentStat {
  type: string;
  avg?: number;
  max?: number;
  min?: number;
}

function markStudentStatsQuery(studentId: string, authId: string | undefined) {
  return {
    queryKey: ["student", studentId, "mark-student-stats", authId ?? "anon"] as const,
    queryFn: () =>
      pb.collection("mark_student_stats").getList<MarkStudentStat>(1, 10, {
        filter: `student = '${studentId}'`,
        skipTotal: true,
        fields: "type,avg,max,min",
      }),
  };
}

export const Route = createFileRoute("/_app/students/$studentId")({
  beforeLoad: ({ params }) => {
    // Students may only view their own profile. currentStudentId() is the
    // student record id denormalized on auth; undefined for teacher/support,
    // who are unrestricted here.
    if (currentRole() === "student" && params.studentId !== currentStudentId()) {
      throw redirect({ to: "/dashboard" });
    }
  },
  loader: ({ context, params }) => {
    const authId = pb.authStore.model?.id;
    return Promise.all([
      context.queryClient.ensureQueryData(studentQuery(params.studentId, authId)),
      context.queryClient.ensureQueryData(rcCountQuery(params.studentId, authId)),
      context.queryClient.ensureQueryData(bestRankQuery(params.studentId, authId)),
      context.queryClient.ensureQueryData(markStudentStatsQuery(params.studentId, authId)),
    ]);
  },
  component: StudentDetailPage,
});

function StudentDetailPage() {
  const { studentId } = Route.useParams();
  const authId = pb.authStore.model?.id;

  const { data: student } = useSuspenseQuery(studentQuery(studentId, authId));
  const { data: rcList } = useSuspenseQuery(rcCountQuery(studentId, authId));
  const { data: bestRankList } = useSuspenseQuery(bestRankQuery(studentId, authId));
  const { data: statsList } = useSuspenseQuery(markStudentStatsQuery(studentId, authId));

  useDocumentTitle(student.name);

  const examAttemptCount = rcList.totalItems;
  const bestRank = bestRankList.items[0]?.rank ?? null;

  const reduced = useReduced();

  const containerVariants: Variants = useMemo(
    () =>
      reduced
        ? { hidden: { opacity: 1 }, visible: { opacity: 1 } }
        : {
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: { staggerChildren: 0.06, delayChildren: 0.04 },
            },
          },
    [reduced],
  );

  const itemVariants: Variants = useMemo(
    () =>
      reduced
        ? { hidden: { opacity: 1, y: 0 }, visible: { opacity: 1, y: 0 } }
        : {
            hidden: { opacity: 0, y: 8 },
            visible: {
              opacity: 1,
              y: 0,
              transition: { type: "spring", stiffness: 220, damping: 24 },
            },
          },
    [reduced],
  );

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="flex flex-col gap-4"
    >
      <motion.div variants={itemVariants}>
        <StudentProfileModern
          student={student}
          examAttemptCount={examAttemptCount}
          bestRank={bestRank}
        />
      </motion.div>

      {examAttemptCount > 0 ? (
        <>
          {/* Report cards now lead — students read individual marks before
              they care about aggregate subject stats or the trend graph. */}
          <motion.section variants={itemVariants} className="flex flex-col gap-3">
            <header className="flex flex-col gap-0.5 px-1">
              <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Exam history
              </span>
              <h3 className="text-lg font-semibold tracking-tight md:text-xl">All report cards</h3>
            </header>
            <ReportCardsList studentId={student.id} studentName={student.name} />
          </motion.section>

          <motion.section variants={itemVariants} className="flex flex-col gap-3">
            <header className="flex flex-col gap-0.5 px-1">
              <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                Subject performance
              </span>
              <h3 className="text-lg font-semibold tracking-tight md:text-xl">
                Mark averages by subject
              </h3>
            </header>
            <ExamStatsCarouselView stats={statsList.items} />
          </motion.section>

          <motion.div variants={itemVariants}>
            <BentoTile eyebrow="Trend" title="Score history">
              <StudentReportCardsGraph studentId={student.id} />
            </BentoTile>
          </motion.div>
        </>
      ) : (
        <motion.section
          variants={itemVariants}
          className="flex items-center gap-3 rounded-2xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground"
        >
          <BadgeAlert className="h-4 w-4 shrink-0" aria-hidden />
          <p>No exams have been conducted yet.</p>
        </motion.section>
      )}
    </motion.div>
  );
}

function BentoTile({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-border/60 bg-card p-5 shadow-[var(--shadow-bento)]">
      <header className="mb-4 flex flex-col gap-0.5">
        <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
          {eyebrow}
        </span>
        <h3 className="text-lg font-semibold tracking-tight md:text-xl">{title}</h3>
      </header>
      {children}
    </section>
  );
}
